import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  Modal,
  TouchableOpacity,
  Platform,
} from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import { Paprika, PaprikaSpacing, PaprikaRadius, PaprikaTypography, PaprikaShadows } from '../../styles/theme';
import { RejectionReason } from '../../redux/userPreferencesSlice';
import { useTranslation } from 'react-i18next';

export type FeedbackContext = 'remove' | 'regenerate';

interface SkipFeedbackModalProps {
  visible: boolean;
  recipeName: string;
  context?: FeedbackContext;
  onSubmit: (reason?: RejectionReason, freeText?: string) => void;
  onSkipFeedback: () => void;
}

const SkipFeedbackModal: React.FC<SkipFeedbackModalProps> = ({
  visible,
  recipeName,
  context = 'remove',
  onSubmit,
  onSkipFeedback,
}) => {
  const { t } = useTranslation();
  const [freeText, setFreeText] = useState('');

  const title = context === 'remove' ? t('skipFeedback.titleRemove') : t('skipFeedback.titleReplace');
  const canSubmit = !!freeText.trim();

  const handleSubmit = () => {
    onSubmit(undefined, freeText.trim() || undefined);
    setFreeText('');
  };

  const handleSkip = () => {
    onSkipFeedback();
    setFreeText('');
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleSkip}
    >
      <TouchableOpacity
        style={styles.overlay}
        activeOpacity={1}
        onPress={handleSkip}
      >
        <TouchableOpacity
          activeOpacity={1}
          onPress={(e) => e.stopPropagation()}
          style={styles.modalContent}
        >
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title} numberOfLines={1} adjustsFontSizeToFit>{title}</Text>
            <Text style={styles.subtitle} numberOfLines={1}>
              {recipeName}
            </Text>
          </View>

          {/* Free-text input */}
          <View style={styles.inputSection}>
            <TextInput
              style={styles.freeTextInput}
              placeholder={context === 'remove'
                ? t('skipFeedback.placeholderRemove')
                : t('skipFeedback.placeholderReplace')}
              placeholderTextColor={Paprika.textMuted}
              value={freeText}
              onChangeText={setFreeText}
              multiline
              maxLength={200}
              textAlignVertical="top"
              autoFocus
            />
            <View style={styles.micHint}>
              <Feather name="mic" size={14} color={Paprika.textMuted} />
              <Text style={styles.micHintText}>{t('photoImport.voiceHint')}</Text>
            </View>
          </View>

          {/* Actions */}
          <View style={styles.actions}>
            <TouchableOpacity
              style={styles.skipFeedbackButton}
              onPress={handleSkip}
              activeOpacity={0.7}
            >
              <Text style={styles.skipFeedbackButtonText}>{t('skipFeedback.skip')}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.submitButton,
                !canSubmit && styles.submitButtonDisabled,
              ]}
              onPress={handleSubmit}
              activeOpacity={0.7}
              disabled={!canSubmit}
            >
              <Text style={styles.submitButtonText}>
                {canSubmit ? t('skipFeedback.submit') : t('skipFeedback.typeSomething')}
              </Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: PaprikaSpacing.lg,
  },
  modalContent: {
    backgroundColor: Paprika.cardBg,
    borderRadius: PaprikaRadius.lg,
    width: '100%',
    maxWidth: 400,
    ...PaprikaShadows.modal,
  },
  header: {
    padding: PaprikaSpacing.lg,
    paddingBottom: PaprikaSpacing.md,
  },
  title: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    marginBottom: PaprikaSpacing.xs,
  },
  subtitle: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
  },
  inputSection: {
    paddingHorizontal: PaprikaSpacing.lg,
    paddingBottom: PaprikaSpacing.md,
  },
  freeTextInput: {
    borderWidth: 1,
    borderColor: Paprika.border,
    borderRadius: PaprikaRadius.md,
    padding: PaprikaSpacing.md,
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textPrimary,
    minHeight: 80,
    backgroundColor: Paprika.surfaceLight,
  },
  micHint: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: PaprikaSpacing.sm,
    gap: 6,
  },
  micHintText: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
  },
  actions: {
    padding: PaprikaSpacing.md,
    paddingHorizontal: PaprikaSpacing.lg,
    paddingBottom: PaprikaSpacing.lg,
    borderTopWidth: 1,
    borderTopColor: Paprika.borderLight,
  },
  skipFeedbackButton: {
    paddingVertical: PaprikaSpacing.md,
    alignItems: 'center',
    marginBottom: PaprikaSpacing.sm,
  },
  skipFeedbackButtonText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.textMuted,
  },
  submitButton: {
    backgroundColor: Paprika.accent,
    paddingVertical: 14,
    borderRadius: PaprikaRadius.xl,
    alignItems: 'center',
  },
  submitButtonDisabled: {
    backgroundColor: Paprika.border,
  },
  submitButtonText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.cardBg,
  },
});

export default SkipFeedbackModal;
